import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import React, { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';

// Reusable Excel generation function (uses pre-fetched data)
const generateFpsExcel = async (fetchedData, tableheading, month, year) => {
    try {
        if (!fetchedData || fetchedData.length === 0) {
            alert("No data to export");
            return;
        }

        const { data } = fetchedData; // already has data and tableheading
        const schemes = Object.keys(data[0].total_cards || {}).filter(k => k !== 'total');
        const commodities = Object.keys(data[0].quantity_sold || {});

        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet('FPS Report');

        // === Calculate total columns ===
        const totalCols = 5 + (schemes.length * 2 + 2) + (schemes.length + 1) + 1 + commodities.reduce((sum, c) => {
            const obj = data[0].quantity_sold[c] || {};
            return sum + Object.keys(obj).filter(k => k !== 'total').length + 1;
        }, 0);

        // Title
        ws.mergeCells(1, 1, 1, totalCols);
        const titleCell = ws.getCell('A1');
        titleCell.value = tableheading || 'Shop Wise Cards and Sale Details';
        titleCell.font = { bold: true, size: 14 };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        ws.getRow(1).height = 30;

        let col = 1;

        // Headers setup (same as your original)
        ws.getCell(2, col).value = 'S.No'; ws.mergeCells(2, col, 4, col); col++;
        ws.getCell(2, col).value = 'District'; ws.mergeCells(2, col, 4, col); col++;
        ws.getCell(2, col).value = 'Office'; ws.mergeCells(2, col, 4, col); col++;
        ws.getCell(2, col).value = 'FPS ID'; ws.mergeCells(2, col, 4, col); col++;
        ws.getCell(2, col).value = 'Active Days'; ws.mergeCells(2, col, 4, col); col++;

        // Total Cards & Units
        const totalCardsUnitsCols = (schemes.length * 2) + 2;
        ws.mergeCells(2, col, 2, col + totalCardsUnitsCols - 1);
        ws.getCell(2, col).value = 'Total Cards & Units';

        schemes.forEach(s => {
            ws.mergeCells(3, col, 3, col + 1);
            ws.getCell(3, col).value = s.toUpperCase();
            ws.getCell(4, col).value = 'Cards';
            ws.getCell(4, col + 1).value = 'Units';
            col += 2;
        });
        ws.mergeCells(3, col, 3, col + 1);
        ws.getCell(3, col).value = 'TOTAL';
        ws.getCell(4, col).value = 'Cards';
        ws.getCell(4, col + 1).value = 'Units';
        col += 2;

        // Avail Cards
        ws.mergeCells(2, col, 3, col + schemes.length);
        ws.getCell(2, col).value = 'Avail Cards';
        schemes.forEach(s => ws.getCell(4, col++).value = s.toUpperCase());
        ws.getCell(4, col++).value = 'TOTAL';

        // Distribution %
        ws.getCell(2, col).value = 'Distribution % (Avail/Total Cards)';
        ws.mergeCells(2, col, 4, col); col++;

        // Quantity Sold
        const qtyStartCol = col;
        ws.getCell(2, col).value = 'Quantity Sold (in Kg)';
        commodities.forEach(comm => {
            const keys = Object.keys(data[0].quantity_sold[comm] || {}).filter(k => k !== 'total').sort();
            ws.mergeCells(3, col, 3, col + keys.length);
            ws.getCell(3, col).value = comm.replace(/_/g, ' ').toUpperCase();
            keys.forEach(k => ws.getCell(4, col++).value = k.toUpperCase());
            ws.getCell(4, col++).value = 'TOTAL';
        });
        ws.mergeCells(2, qtyStartCol, 2, col - 1);

        // === TOTALS ===
        let totals = {
            login_days: 0,
            total_cards: {}, total_units: {}, avail_cards: {},
            quantity_sold: {}
        };

        schemes.forEach(s => {
            totals.total_cards[s] = 0; totals.total_units[s] = 0; totals.avail_cards[s] = 0;
        });
        totals.total_cards.total = 0; totals.total_units.total = 0; totals.avail_cards.total = 0;

        commodities.forEach(comm => {
            totals.quantity_sold[comm] = {};
            const keys = Object.keys(data[0].quantity_sold[comm] || {}).filter(k => k !== 'total');
            keys.forEach(k => totals.quantity_sold[comm][k] = 0);
            totals.quantity_sold[comm].total = 0;
        });

        // === Fill Data + Calculate Totals ===
        data.forEach((row, i) => {
            const r = ws.addRow();
            let c = 1;
            r.getCell(c++).value = i + 1;
            r.getCell(c++).value = row.dist_name_en;
            r.getCell(c++).value = row.afso_name_en;
            r.getCell(c++).value = row.fps_id;
            r.getCell(c++).value = row.login_days;

            schemes.forEach(s => {
                r.getCell(c++).value = row.total_cards[s] || 0;
                r.getCell(c++).value = row.total_units[s] || 0;
            });
            r.getCell(c++).value = row.total_cards.total || 0;
            r.getCell(c++).value = row.total_units.total || 0;

            schemes.forEach(s => r.getCell(c++).value = row.avail_cards[s] || 0);
            r.getCell(c++).value = row.avail_cards.total || 0;

            r.getCell(c++).value = row.avail_per != null ? Number(row.avail_per).toFixed(2) + '%' : '0%';

            commodities.forEach(comm => {
                const qty = row.quantity_sold[comm] || {};
                const keys = Object.keys(qty).filter(k => k !== 'total').sort();
                keys.forEach(k => r.getCell(c++).value = qty[k] || 0);
                r.getCell(c++).value = qty.total || 0;
            });

            // Accumulate
            totals.login_days += (row.login_days || 0);
            schemes.forEach(s => {
                totals.total_cards[s] += (row.total_cards[s] || 0);
                totals.total_units[s] += (row.total_units[s] || 0);
                totals.avail_cards[s] += (row.avail_cards[s] || 0);
            });
            totals.total_cards.total += (row.total_cards.total || 0);
            totals.total_units.total += (row.total_units.total || 0);
            totals.avail_cards.total += (row.avail_cards.total || 0);

            commodities.forEach(comm => {
                const qty = row.quantity_sold[comm] || {};
                Object.keys(qty).filter(k => k !== 'total').forEach(k => {
                    totals.quantity_sold[comm][k] += (qty[k] || 0);
                });
                totals.quantity_sold[comm].total += (qty.total || 0);
            });
        });

        // Total Distribution %
        const availPer = totals.total_cards.total > 0
            ? ((totals.avail_cards.total / totals.total_cards.total) * 100).toFixed(2) + '%'
            : '0%';

        // Total Row
        const totalRow = ws.addRow();
        let tc = 1;
        totalRow.getCell(tc++).value = '';
        totalRow.getCell(tc++).value = '';
        totalRow.getCell(tc++).value = '';
        totalRow.getCell(tc++).value = 'TOTAL';
        totalRow.getCell(tc++).value = totals.login_days;

        schemes.forEach(s => {
            totalRow.getCell(tc++).value = totals.total_cards[s];
            totalRow.getCell(tc++).value = totals.total_units[s];
        });
        totalRow.getCell(tc++).value = totals.total_cards.total;
        totalRow.getCell(tc++).value = totals.total_units.total;

        schemes.forEach(s => totalRow.getCell(tc++).value = totals.avail_cards[s]);
        totalRow.getCell(tc++).value = totals.avail_cards.total;
        totalRow.getCell(tc++).value = availPer;

        commodities.forEach(comm => {
            Object.keys(totals.quantity_sold[comm]).filter(k => k !== 'total').sort().forEach(k => {
                totalRow.getCell(tc++).value = totals.quantity_sold[comm][k];
            });
            totalRow.getCell(tc++).value = totals.quantity_sold[comm].total;
        });

        // Styling (apply same fill/font to headers as footer)
        const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF9900' } };
        const headerFont = { bold: true, color: { argb: 'FF000000 ' } };

        // Title row (row 1) keep larger size but same fill/color
        titleCell.fill = headerFill;
        titleCell.font = { bold: true, size: 14, color: { argb: 'FF000000 ' } };

        // Header rows (2-4) - same look as footer
        [2, 3, 4].forEach(n => {
            const row = ws.getRow(n);
            row.eachCell(cell => {
                cell.fill = headerFill;
                cell.font = headerFont;
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        // Footer (total) styling remains as before
        totalRow.font = { bold: true, color: { argb: 'FF000000' } };
        totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF9900' } };
        totalRow.eachCell(cell => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'double' }, right: { style: 'thin' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        ws.columns.forEach(col => col.width = 15);
        // remove the previous simple bold-setting for rows 2-4 (now handled above)
        // ...existing code...
        ws.eachRow(row => {
            row.eachCell(cell => {
                // keep general borders/alignment for all cells (won't override fill/font)
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, `FPS_Report_${month}_${year}.xlsx`);

    } catch (error) {
        console.error('Excel generation failed:', error);
        alert('Failed to generate Excel file: ' + (error.message || 'Unknown error'));
    }
};

const ShopWiseCardandSaleDetailsExcel = ({ month, year, dist_code, afso_code }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null); // { data: [], tableheading: "" }
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!month || !year || !dist_code) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_URL_FPS}/api/Fps/getdataforexcel`, {
                    params: {
                        month: parseInt(month, 10),
                        year: parseInt(year, 10),
                        distCode: dist_code,
                        afsoCode: afso_code || undefined // send only if exists
                    }
                });

                if (response.data.success && response.data.data?.length > 0) {
                    setData(response.data); // contains { data: [...], tableheading: "..." }
                } else {
                    setData(null);
                }
            } catch (err) {
                console.error("Error fetching data:", err);
                setError(err.response?.data?.message || "Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [month, year, dist_code, afso_code]); // afso_code included

    if (loading) return <div align="center"><CircularProgress /></div>;
    if (error) return <div align="center" className='response'>{error}</div>;
    if (!data) return <div align="center" className='response'>No Data Found</div>;

    return (
        <div align="center" style={{ marginTop: "20px" }}>
            <button
                className="button button2"
                onClick={() => generateFpsExcel(data, data.tableheading, month, year)}
            >
                Excel Download
            </button>
        </div>
    );
};

export default ShopWiseCardandSaleDetailsExcel;